import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQuery } from '../api';
import { endpoints } from '../endpoints';

import type { TDateRangePresetKey } from '../../modules/insights/insights/helpers/datePresets';
import type { THygieneTaskCode, TProviderRole, TTreatmentType } from './types';

export type { TDateRangePresetKey };

export const useGetInsightsSavedViews = () => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  const insightsSavedViewsPath = `${endpoints.insightsSavedViews}/`;

  return useQuery([insightsSavedViewsPath, orgId], () =>
    makeAPIRequestReactQuery<{ data: TInsightsSavedView[] }>(
      insightsSavedViewsPath,
      {},
      'get',
      {
        headers: {
          Org: orgId,
        },
      },
    ),
  );
};

export const useCreateInsightsSavedView = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  const insightsSavedViewsPath = `${endpoints.insightsSavedViews}/`;

  return useMutation({
    mutationFn: async (data: { name: string; view: object }) => {
      const { name, view } = data;

      await makeAPIRequestReactQuery<{ data: TInsightsSavedView[] }>(
        insightsSavedViewsPath,
        {},
        'POST',
        {
          headers: {
            Org: orgId,
          },
          data: {
            name: name,
            view: view,
          },
        },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([insightsSavedViewsPath]);
      onSuccess?.();
    },
  });
};

export const useUpdateInsightsSavedView = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  const insightsSavedViewsPath = `${endpoints.insightsSavedViews}/`;

  return useMutation({
    mutationFn: async (data: { id: string; name: string; view: object }) => {
      const { id, name, view } = data;

      await makeAPIRequestReactQuery<{ data: TInsightsSavedView[] }>(
        insightsSavedViewsPath,
        {},
        'PUT',
        {
          headers: {
            Org: orgId,
          },
          data: {
            id: id,
            name: name,
            view: view,
          },
        },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([insightsSavedViewsPath]);
      onSuccess?.();
    },
  });
};

export const useDeleteInsightsSavedView = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  const insightsSavedViewsPath = `${endpoints.insightsSavedViews}/`;

  return useMutation({
    mutationFn: async (data: { id: string }) => {
      const { id } = data;

      await makeAPIRequestReactQuery<{ data: TInsightsSavedView[] }>(
        insightsSavedViewsPath,
        {
          id: id,
        },
        'DELETE',
        {
          headers: {
            Org: orgId,
          },
        },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([insightsSavedViewsPath]);
      onSuccess?.();
    },
  });
};

export type TInsightsSavedView = {
  id: string;
  organizationId: string;
  name: string;
  view: TInsightsSavedViewConfig;
  updatedBy: string;
  updatedAt: string;
};

export type TInsightsSavedViewConfig = {
  practices?: string[];
  regions?: string[];
  providerRole?: TProviderRole[];
  taskType?: THygieneTaskCode[];
  txType?: TTreatmentType[];
  materialityFilterProductionDollars?: number;
  materialityFilterProductionCounts?: number;
  materialityFilterOpportunityDollars?: number;
  materialityFilterOpportunityCounts?: number;
  isCounts?: boolean;
  startDate?: Date;
  endDate?: Date;
  /** If true, start/end are ignored on load and recalculated from dateRangePreset relative to today */
  relativeDateRange?: boolean;
  dateRangePreset?: TDateRangePresetKey;
  /** How the previous (comparison) period is determined: trailing | yoy | custom */
  comparisonPeriodMode?: string;
  priorStartDate?: Date;
  priorEndDate?: Date;
  selectedDashboard?: string;
  providerDrilldown?: string;
  practiceDrilldown?: string;
  regionDrilldown?: string;
};
